import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faEdit, faHome } from "@fortawesome/free-solid-svg-icons";
import { Breadcrumb, Button, Card, Col, Row, Table } from "@themesberg/react-bootstrap";
import { useHistory, useParams } from "react-router-dom";

import { getProductById } from "../api/products.api";
import { useToast } from "../components/ToastProvider";
import { Routes } from "../routes";
import { resolveAssetUrl } from "../utils/assetUrl";

const formatDate = (dateValue) => {
  if (!dateValue) return "N/A";
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return "N/A";
  return date.toLocaleString();
};

const ProductDetailsSkeleton = () => (
  <>
    <Card border="light" className="shadow-sm mb-4">
      <Card.Body>
        <Row>
          <Col xs={12} lg={4} className="mb-4 mb-lg-0">
            <div className="d-flex flex-wrap gap-3">
              <div className="skeleton-text" style={{ width: "140px", height: "140px", borderRadius: "0.75rem" }} />
              <div className="skeleton-text" style={{ width: "140px", height: "140px", borderRadius: "0.75rem" }} />
            </div>
          </Col>
          <Col xs={12} lg={8}>
            <div className="skeleton-text" style={{ width: "200px", height: "20px", marginBottom: "12px" }} />
            <div className="skeleton-text" style={{ width: "100%", height: "14px", marginBottom: "8px" }} />
            <div className="skeleton-text" style={{ width: "80%", height: "14px", marginBottom: "8px" }} />
            <div className="skeleton-text" style={{ width: "40%", height: "14px", marginBottom: "16px" }} />
            <div className="skeleton-text" style={{ width: "120px", height: "14px", marginBottom: "8px" }} />
            <div className="skeleton-text" style={{ width: "120px", height: "14px" }} />
          </Col>
        </Row>
      </Card.Body>
    </Card>

    <Card border="light" className="table-wrapper table-responsive shadow-sm">
      <Card.Header>
        <div className="skeleton-text" style={{ width: "80px", height: "18px" }} />
      </Card.Header>
      <Card.Body className="pt-0">
        <Table hover className="user-table align-items-center">
          <thead>
            <tr>
              <th className="border-bottom">Image</th>
              <th className="border-bottom">Variant</th>
              <th className="border-bottom">SKU</th>
              <th className="border-bottom">Stock</th>
              <th className="border-bottom">Low Stock Threshold</th>
            </tr>
          </thead>
          <tbody>
            {[...Array(5)].map((_, index) => (
              <tr key={index}>
                <td><div className="skeleton-text" style={{ width: "52px", height: "52px", borderRadius: "0.5rem" }} /></td>
                <td><div className="skeleton-text" style={{ width: "100px", height: "12px" }} /></td>
                <td><div className="skeleton-text" style={{ width: "80px", height: "12px" }} /></td>
                <td><div className="skeleton-text" style={{ width: "50px", height: "12px" }} /></td>
                <td><div className="skeleton-text" style={{ width: "80px", height: "12px" }} /></td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Card.Body>
    </Card>
  </>
);

export default () => {
  const history = useHistory();
  const { id } = useParams();
  const { showToast } = useToast();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadProduct = async () => {
      setLoading(true);

      try {
        const response = await getProductById(id);
        setProduct(response);
      } catch (error) {
        showToast({ message: error.message || "Failed to load product details", variant: "danger" });
      } finally {
        setLoading(false);
      }
    };

    loadProduct();
  }, [id, showToast]);

  return (
    <>
      <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4">
        <div className="d-block mb-4 mb-md-0">
          <Breadcrumb className="d-none d-md-inline-block" listProps={{ className: "breadcrumb-dark breadcrumb-transparent" }}>
            <Breadcrumb.Item><FontAwesomeIcon icon={faHome} /></Breadcrumb.Item>
            <Breadcrumb.Item onClick={() => history.push(Routes.Products.path)}>Products</Breadcrumb.Item>
            <Breadcrumb.Item active>Details</Breadcrumb.Item>
          </Breadcrumb>
          <h4>Product Details</h4>
        </div>
        <div className="btn-toolbar mb-2 mb-md-0">
          <Button variant="outline-secondary" className="me-2" onClick={() => history.push(Routes.Products.path)}>
            <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
            Back
          </Button>
          <Button variant="primary" onClick={() => history.push(`${Routes.Products.path}/${id}/edit`)}>
            <FontAwesomeIcon icon={faEdit} className="me-2" />
            Edit Product
          </Button>
        </div>
      </div>
      {loading ? (
        <ProductDetailsSkeleton />
      ) : product ? (
        <>
          <Card border="light" className="shadow-sm mb-4">
            <Card.Body>
              <Row>
                <Col xs={12} lg={4} className="mb-4 mb-lg-0">
                  {Array.isArray(product.images) && product.images.length > 0 ? (
                    <div className="d-flex flex-wrap gap-3">
                      {product.images.map((image) => (
                        <img
                          key={image.id || image.image_url}
                          src={resolveAssetUrl(image.image_url)}
                          alt={product.name}
                          style={{ width: "140px", height: "140px", borderRadius: "0.75rem", objectFit: "cover" }}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className="border rounded d-flex align-items-center justify-content-center text-muted" style={{ width: "100%", maxWidth: "320px", minHeight: "220px" }}>
                      No image
                    </div>
                  )}
                </Col>
                <Col xs={12} lg={8}>
                  <h5>{product.name}</h5>
                  <p className="mb-3">{product.description || "No description provided."}</p>
                  <p className="mb-3"><strong>Images:</strong> {Array.isArray(product.images) ? product.images.length : 0}</p>
                  <p className="mb-1"><strong>Created:</strong> {formatDate(product.created_at)}</p>
                  <p className="mb-0"><strong>Updated:</strong> {formatDate(product.updated_at)}</p>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          <Card border="light" className="table-wrapper table-responsive shadow-sm">
            <Card.Header>
              <h5 className="mb-0">Variants</h5>
            </Card.Header>
            <Card.Body className="pt-0">
              <Table hover className="user-table align-items-center">
                <thead>
                  <tr>
                    <th className="border-bottom">Image</th>
                    <th className="border-bottom">Variant</th>
                    <th className="border-bottom">SKU</th>
                    <th className="border-bottom">Stock</th>
                    <th className="border-bottom">Low Stock Threshold</th>
                  </tr>
                </thead>
                <tbody>
                  {!product || product.variants.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="text-center py-5">No variants found.</td>
                    </tr>
                  ) : (
                    product.variants.map((variant) => (
                      <tr key={variant.id}>
                        <td>
                          {Array.isArray(variant.images) && variant.images.length > 0 ? (
                            <div className="d-flex flex-wrap gap-2">
                              {variant.images.map((image) => (
                                <img
                                  key={image.id || image.image_url}
                                  src={resolveAssetUrl(image.image_url)}
                                  alt={variant.variant_name}
                                  style={{ width: "52px", height: "52px", objectFit: "cover", borderRadius: "0.5rem" }}
                                />
                              ))}
                            </div>
                          ) : "N/A"}
                        </td>
                        <td>{variant.variant_name}</td>
                        <td>{variant.sku}</td>
                        <td>{variant.stock}</td>
                        <td>{variant.low_stock_threshold}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </>
      ) : null}
    </>
  );
};